import katex from "katex";
import "katex/dist/katex.min.css";

/**
 * Render a string that mixes plain text with LaTeX math.
 *   - $$...$$  → block (display) math
 *   - $...$    → inline math
 * Non-math text is preserved verbatim (including line breaks). If a math
 * fragment fails to parse, the original `$...$` source is shown as a fallback
 * so nothing silently disappears.
 *
 * KaTeX's renderToString emits <span> markup (even in display mode), so the
 * output is safe to nest inside <p>/<span>. Input is LaTeX, not user HTML.
 */
function renderMath(src, display) {
  try {
    const cleaned = String(src)
      // Repair over-escaped commands from JSON double-escaping: `\\frac` → `\frac`
      // (a doubled backslash before a command letter is almost never a real LaTeX
      // line break in our content, so collapsing it fixes broken math reliably).
      .replace(/\\\\(?=[a-zA-Z])/g, "\\")
      // A fill-in blank written as underscores breaks KaTeX: in math mode `__` is
      // a double subscript (parse error → KaTeX prints the raw red source). Turn a
      // `\text{___}` or a bare run of underscores into a drawn blank line instead.
      // The lift is required: an un-lifted \rule sits at the true baseline only
      // when something precedes it on the line (e.g. "2 × 4 = ___"); as the FIRST
      // atom in the expression (e.g. "___ × 4 = 16") KaTeX renders it noticeably
      // higher, floating above where the rest of the line sits. -0.3em was
      // measured (pixel-checked against a true-baseline marker) to land both
      // positions on the baseline in the actual bold question-text weight used
      // sitewide — -0.2em still sits visibly high in bold text.
      .replace(/\\text\s*\{\s*_+\s*\}/g, "\\rule[-0.3em]{2.4em}{0.4pt}")
      .replace(/_{2,}/g, "\\rule[-0.3em]{2.4em}{0.4pt}");
    // Force display-size sizing even inline: inline (textstyle) fractions render
    // tiny and cramped, so a `\frac` bar reads like a strike-through. `\displaystyle`
    // makes fractions full size and legible while still flowing in the line
    // (displayMode stays false, so there's no centered block).
    const html = katex.renderToString(`\\displaystyle ${cleaned}`, { throwOnError: false, displayMode: display });
    // If KaTeX couldn't parse it (renders the source in its red error span), treat
    // it as a failure so the caller can fall back instead of showing red LaTeX.
    if (/katex-error/.test(html)) return null;
    return html;
  } catch {
    return null;
  }
}

// Build a span for rendered KaTeX. Fractions sit on the math axis (the bar lands
// on the text baseline), so a display-size `\frac` hangs low; `vertical-align:
// middle` lifts it to sit centered with the surrounding text.
function mathSpan(html, key, className) {
  const style = /mfrac|frac-line/.test(html) ? { verticalAlign: "middle" } : undefined;
  return (
    <span key={key} className={className} style={style} dangerouslySetInnerHTML={{ __html: html }} />
  );
}

// The model sometimes emits a bare LaTeX expression with NO $...$ delimiters
// (common for pure calculation questions, e.g. "\frac{2}{7} + \frac{3}{7} = ..."),
// which would otherwise show as raw backslash source. Detect a string that is
// clearly a LaTeX expression so we can render the whole thing as math.
function looksLikeBareLatex(s) {
  return /\\(?:frac|dfrac|tfrac|sqrt|times|div|cdot|text|left|right|begin|sum|int|pi|theta|alpha|beta|angle|circ|le|ge|ne|neq|leq|geq|pm|mp|approx|cdots|ldots)\b|\\[a-zA-Z]+\s*\{|[\^_]\s*\{/.test(s);
}

// Deterministic (non-AI) generators write plain-Unicode math — "x²", "√49",
// "b^t", "x₁" — instead of LaTeX, unlike the AI pipeline (which is
// instructed to always $-delimit). Rather than touching every generator,
// upgrade that notation to real LaTeX here so it typesets consistently
// everywhere MathText is used. Only runs on strings with no `$` already
// (pre-existing LaTeX is left completely alone).
const SUP_TO_ASCII = { "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9", "⁻": "-" };
const SUB_TO_ASCII = { "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4", "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9" };

// Unit abbreviations that legitimately end in a squared/cubed letter (cm²,
// km³, ft², yd², mi²) — keyed by that last letter, valued by the letter(s)
// that can precede it to form the unit. Checked so "5 g/cm³" doesn't get
// split into "c" + a stacked "m³" exponent.
const UNIT_PRECEDING_LETTER = { m: ["c", "k", "m"], t: ["f"], d: ["y"], i: ["m"] };

// Raw (undelimited) super/subscript conversion, used for content nested
// inside a \sqrt{} or \frac{} so it isn't re-matched (and re-wrapped) by the
// standalone passes below.
function convertSupSub(s) {
  s = s.replace(/([A-Za-z0-9)\]])([⁰¹²³⁴⁵⁶⁷⁸⁹⁻]+)/g, (match, base, sup, offset, str) => {
    if (UNIT_PRECEDING_LETTER[base]?.includes(str[offset - 1])) return match;
    return `${base}^{${sup.split("").map((c) => SUP_TO_ASCII[c] ?? c).join("")}}`;
  });
  s = s.replace(/([A-Za-z])([₀₁₂₃₄₅₆₇₈₉]+)/g, (_, base, sub) =>
    `${base}_{${sub.split("").map((c) => SUB_TO_ASCII[c] ?? c).join("")}}`);
  return s;
}

// Raw radical + super/subscript conversion for content nested inside a
// \frac{} numerator/denominator (e.g. the "√x" in "(√x − 5) / (x − 3)").
function convertRawMath(s) {
  s = s.replace(/([√∛])\(([^()]+)\)/g, (_, r, inner) => `${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${convertSupSub(inner)}}`);
  s = s.replace(/([√∛])(\d+(?:\.\d+)?)/g, (_, r, n) => `${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${n}}`);
  s = s.replace(/([√∛])([A-Za-z][A-Za-z0-9]*)/g, (_, r, v) => `${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${convertSupSub(v)}}`);
  return convertSupSub(s);
}

function autoLatexify(text) {
  if (text.includes("$")) return text;
  let s = text;

  // Fractions & rational expressions first — (num)/(den) with parenthesized
  // sides, or bare term/term where at least one side has a digit (so units
  // like "g/cm³", derivative notation "dy/dx", and phrases like "and/or",
  // which have no digit on either side, are left alone). Nested radicals/
  // exponents inside each side are converted raw before the fraction is
  // $-wrapped, so the passes below can't double-wrap the same characters.
  s = s.replace(/\(([^()]+)\)\s*\/\s*\(([^()]+)\)/g, (_, num, den) =>
    `$\\frac{${convertRawMath(num)}}{${convertRawMath(den)}}$`);
  s = s.replace(/(-?[A-Za-z0-9²³⁴⁵⁶⁷⁸⁹⁰¹⁻√∛]+)\s*\/\s*(-?[A-Za-z0-9²³⁴⁵⁶⁷⁸⁹⁰¹⁻√∛]+)/g, (match, num, den) =>
    /\d/.test(num + den) ? `$\\frac{${convertRawMath(num)}}{${convertRawMath(den)}}$` : match);

  // Radicals, converting any superscripts/subscripts inside the radical
  // as part of the same wrap — this "consumes" them so the standalone passes
  // below can't double-wrap the same characters.
  s = s.replace(/([√∛])\(([^()]+)\)/g, (_, r, inner) => `$${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${convertSupSub(inner)}}$`);
  s = s.replace(/([√∛])\[([^[\]]+)\]/g, (_, r, inner) => `$${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${convertSupSub(inner)}}$`);
  s = s.replace(/([√∛])(\d+(?:\.\d+)?)/g, (_, r, n) => `$${r === "∛" ? "\\sqrt[3]" : "\\sqrt"}{${n}}$`);

  // Standalone superscripts/subscripts left outside any radical.
  s = s.replace(/([A-Za-z0-9)\]])([⁰¹²³⁴⁵⁶⁷⁸⁹⁻]+)/g, (match, base, sup, offset, str) => {
    if (UNIT_PRECEDING_LETTER[base]?.includes(str[offset - 1])) return match;
    return `$${base}^{${sup.split("").map((c) => SUP_TO_ASCII[c] ?? c).join("")}}$`;
  });
  s = s.replace(/([A-Za-z])([₀₁₂₃₄₅₆₇₈₉]+)/g, (_, base, sub) =>
    `$${base}_{${sub.split("").map((c) => SUB_TO_ASCII[c] ?? c).join("")}}$`);

  // Bare-caret exponents some generators use in hints, e.g. "b^t", "(1+r)^t".
  // Runs last: by now any "^{...}" already inserted above has a "{" right
  // after the caret, which this pattern's exponent group can't match, so it
  // can't re-wrap what the previous passes already produced.
  s = s.replace(/(\([^()]+\)|[A-Za-z0-9])\^(\([^()]+\)|[A-Za-z0-9]+)/g, (_, base, exp) => {
    const cleanExp = exp.startsWith("(") && exp.endsWith(")") ? exp.slice(1, -1) : exp;
    return `$${base}^{${cleanExp}}$`;
  });

  return s;
}

export default function MathText({ children, className }) {
  const rawText = String(children ?? "");
  const text = rawText.includes("$") ? rawText : autoLatexify(rawText);

  // No $...$ markers but the string is clearly LaTeX → render it all as math so
  // raw "\frac{}{}" source never leaks through. Falls through on a parse failure.
  if (!text.includes("$") && looksLikeBareLatex(text)) {
    const html = renderMath(text, false);
    if (html != null) {
      return mathSpan(html, undefined, className);
    }
  }

  const re = /\$\$([^$]+?)\$\$|\$([^$]+?)\$/g;

  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: "text", value: text.slice(last, m.index) });
    if (m[1] != null) parts.push({ kind: "block", value: m[1] });
    else parts.push({ kind: "inline", value: m[2] });
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });

  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.kind === "text") {
          return (
            <span key={i} className="whitespace-pre-wrap">
              {p.value}
            </span>
          );
        }
        // Render even $$...$$ inline: display mode is a centered block, which
        // breaks a worksheet line like "expr = ______" onto two lines and shoves
        // the math to the center. Inline keeps it flowing left with the blank.
        const html = renderMath(p.value, false);
        if (html == null) {
          const fence = p.kind === "block" ? "$$" : "$";
          return (
            <span key={i}>
              {fence}
              {p.value}
              {fence}
            </span>
          );
        }
        return mathSpan(html, i);
      })}
    </span>
  );
}
